import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env.local file.'
  );
}

// Create optimized Supabase client
// Optimizations to reduce refresh token 500 errors:
// - Explicit storage adapter (prevents conflicts)
// - Custom storage key (avoids collisions)
// - PKCE flow (more secure, better refresh handling)
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Explicit storage adapter for better session management
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    // Custom storage key to avoid collisions with other apps
    storageKey: 'alpha01-supabase-auth-token',
    // PKCE flow - more secure and better for SPAs (handles refresh better)
    flowType: 'pkce',
  },
  // Global settings
  global: {
    headers: {
      'x-client-info': 'alpha01-web'
    }
  }
});

// Helper function to refresh session with retry logic
// Implements exponential backoff for transient errors (500, timeouts, network issues)
export async function refreshSessionWithRetry(currentSession, maxRetries = 3) {
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (import.meta.env.DEV) console.log(`[Supabase] Refresh attempt ${attempt}/${maxRetries}`);
      
      // Set timeout for refresh request (5 seconds)
      const refreshPromise = supabase.auth.refreshSession(currentSession);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Refresh timeout')), 5000)
      );
      
      const { data, error } = await Promise.race([
        refreshPromise,
        timeoutPromise,
      ]);
      
      if (error) {
        throw error;
      }
      
      if (data?.session) {
        if (import.meta.env.DEV) console.log('[Supabase] Refresh successful');
        return { data, error: null };
      }
      
      throw new Error('No session returned from refresh');
      
    } catch (error) {
      lastError = error;
      console.warn(`[Supabase] Refresh attempt ${attempt} failed:`, error.message);
      
      // If it's a transient error (500, timeout, network), retry with exponential backoff
      if (isTransientError(error)) {
        if (attempt < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s
          const delayMs = Math.pow(2, attempt - 1) * 1000;
          if (import.meta.env.DEV) console.log(`[Supabase] Retrying in ${delayMs}ms...`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
          continue;
        }
      } else {
        // For non-transient errors (like invalid token), don't retry
        console.error('[Supabase] Non-transient error, not retrying:', error);
        break;
      }
    }
  }
  
  // All retries failed
  console.error('[Supabase] All refresh attempts failed:', lastError);
  return { data: null, error: lastError };
}

// Helper to check if error is transient (should retry)
export function isTransientError(error) {
  if (!error) return false;
  
  const message = error.message?.toLowerCase() || '';
  const status = error.status;
  
  return (
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504 ||
    message.includes('timeout') ||
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('econnrefused') ||
    message.includes('500')
  );
}

// Helper to get current user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Helper to get user profile from database
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
  
  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
  
  return data;
};
